import { useTranslation } from 'react-i18next'
import type { HeatmapPhase } from './useHeatmapLayer'

/** What the indicator needs to know about one surveillance area. */
export interface HeatmapIndicatorArea {
  contextId: string
  name: string
  phase: HeatmapPhase
  retry: () => void
}

// A floating pill per area rather than a modal: the map stays pannable while
// a layer is being (re)built, which is the whole point of showing the stale
// or absent layer underneath it in the meantime. 'error' and 'stalled' both
// leave the user stuck without a way to act — the base map is still fine on
// its own, so the recovery is a retry control on that area's own pill, not a
// full-screen takeover that would throw away a perfectly working map.
//
// One row per area, and only for areas that are not ready: Tanger builds each
// context's layer separately, so one area routinely still processes while
// another already renders. A single combined pill could only say "something
// is updating", which is the one thing an operator does not need to be told.
export function HeatmapIndicator({ areas }: { areas: HeatmapIndicatorArea[] }) {
  const { t } = useTranslation()
  const pending = areas.filter((area) => area.phase !== 'ready')
  if (pending.length === 0) return null

  return (
    <div className="asf-indicator-stack">
      {pending.map((area) => {
        const actionable = area.phase === 'error' || area.phase === 'stalled'
        const status =
          area.phase === 'error'
            ? t('heatmap_unavailable')
            : area.phase === 'stalled'
              ? t('still_processing')
              : t('updating')

        return (
          <div
            key={area.contextId}
            className={
              actionable ? 'asf-indicator asf-indicator-actionable' : 'asf-indicator'
            }
            role="status"
            aria-live="polite"
          >
            {/* The area is named on every row, not only when several are
                pending: the row exists to say which area is not ready, and
                that is exactly as useful when it is the only one. */}
            <span>{t('area_status', { name: area.name, status })}</span>
            {actionable && (
              <button type="button" className="asf-indicator-retry" onClick={area.retry}>
                {t('retry')}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
